/**
 * Inline editing for offer unit rows and price rows.
 * Replaces the old edit-unit and edit-price modals.
 */

import { DateRangePicker, DateRangePickerLocale } from '../lib/date-range-picker';
import { postJson, fetchJson } from '../lib/api-client';
import { showNotification } from '../lib/helpers';

interface UnitFormDataResponse {
    success: boolean;
    unitTypes: Array<{ id: number; name: string }>;
    classifications: Array<{ key: string; name: string }>;
    services: Array<{ key: string; name: string }>;
}

const ACTIVE_SVG   = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="32" stroke-width="1.5" stroke="#10B981"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>';
const INACTIVE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="32" stroke-width="1.5" stroke="#E11D48"><path stroke-linecap="round" stroke-linejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>';

function populateSelectIfEmpty(
    sel: HTMLSelectElement | null,
    options: Array<{ value: string; text: string }>,
    currentValue: string
): void {
    if (!sel || sel.options.length > 0) return;
    sel.add(new Option('—', '', currentValue === '', currentValue === ''));
    options.forEach(o => {
        const isSel = o.value === currentValue;
        sel.add(new Option(o.text, o.value, isSel, isSel));
    });
}

function storeOriginals(row: HTMLElement): void {
    row.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.cell-edit').forEach(el => {
        (el as any)._originalValue = el.value;
    });
}

function restoreOriginals(row: HTMLElement): void {
    row.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.cell-edit').forEach(el => {
        el.value = (el as any)._originalValue ?? el.value;
    });
}

function syncToggleButtons(row: HTMLElement): void {
    row.querySelectorAll<HTMLElement>('.status-toggle-group').forEach(group => {
        const hidden = group.querySelector<HTMLInputElement>('input[type="hidden"]');
        if (!hidden) return;
        const val = hidden.value;
        group.querySelectorAll<HTMLButtonElement>('.status-toggle-btn').forEach(btn => {
            const pressed = btn.dataset.value === val;
            btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
            btn.classList.toggle('active', pressed);
        });
    });
}

export function init(): void {
    const indexContainer   = document.getElementById('offer-index-container');
    const detailsContainer = document.getElementById('offer-details-container');
    const container = indexContainer ?? detailsContainer;
    if (!container) return;

    const urlUnitFormData = container.dataset.urlUnitFormData ?? '';
    const urlEditUnit     = container.dataset.urlEditUnit     ?? '';
    const urlEditPrice    = container.dataset.urlEditPrice    ?? '';

    const formDataCache: Record<number, UnitFormDataResponse> = {};

    function buildLocale(el: HTMLElement): DateRangePickerLocale {
        const months = el.dataset.locMonths?.split(',');
        const days   = el.dataset.locDays?.split(',');
        return {
            months:      months?.length === 12 ? months : undefined,
            days:        days?.length   === 7  ? days   : undefined,
            apply:       el.dataset.locApply,
            clear:       el.dataset.locClear,
            placeholder: el.dataset.locSelectDates,
            selectStart: el.dataset.locSelectStart,
        };
    }

    const locActive   = () => container.dataset.locActive   ?? 'Active';
    const locInactive = () => container.dataset.locInactive ?? 'Inactive';

    // ── Delegated click handler ───────────────────────
    document.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement;

        // ╔═══════════════════════════════════════════╗
        // ║  Unit inline edit — enter edit mode       ║
        // ╚═══════════════════════════════════════════╝
        if (target.closest('.btn-edit-unit-inline')) {
            if (!window.matchMedia('(min-width: 992px)').matches) return; // narrow screens use the modal
            const row = target.closest<HTMLTableRowElement>('.unit-row');
            if (!row || row.classList.contains('editing')) return;

            const offerId = parseInt(row.dataset.offerId ?? '0', 10);

            if (!formDataCache[offerId]) {
                row.style.opacity = '0.5';
                try {
                    const data = await fetchJson<UnitFormDataResponse>(`${urlUnitFormData}?offerId=${offerId}`);
                    if (data.success) formDataCache[offerId] = data;
                } catch { /* ignore */ }
                row.style.opacity = '';
            }

            const fd = formDataCache[offerId];
            if (fd) {
                const typeId     = row.dataset.typeId         ?? '';
                const classVal   = row.dataset.classification ?? '';
                const serviceVal = row.dataset.service        ?? '';

                populateSelectIfEmpty(
                    row.querySelector<HTMLSelectElement>('.cell-type-select'),
                    fd.unitTypes.map(t => ({ value: String(t.id), text: t.name })),
                    typeId
                );
                populateSelectIfEmpty(
                    row.querySelector<HTMLSelectElement>('.cell-class-select'),
                    fd.classifications.map(c => ({ value: c.key, text: c.name })),
                    classVal
                );
                populateSelectIfEmpty(
                    row.querySelector<HTMLSelectElement>('.cell-service-select'),
                    fd.services.map(s => ({ value: s.key, text: s.name })),
                    serviceVal
                );
            }

            // Set select values from data attributes (options are now present)
            const typeSelect   = row.querySelector<HTMLSelectElement>('.cell-type-select');
            const classSelect  = row.querySelector<HTMLSelectElement>('.cell-class-select');
            const serviceSelect = row.querySelector<HTMLSelectElement>('.cell-service-select');
            const activeSelect = row.querySelector<HTMLSelectElement>('[name="active"]');
            if (typeSelect)    typeSelect.value    = row.dataset.typeId         ?? '';
            if (classSelect)   classSelect.value   = row.dataset.classification ?? '';
            if (serviceSelect) serviceSelect.value = row.dataset.service        ?? '';
            if (activeSelect)  activeSelect.value  = row.dataset.active         ?? 'true';
            syncToggleButtons(row);

            storeOriginals(row);
            row.classList.add('editing');
            return;
        }

        // ╔═══════════════════════════════════════════╗
        // ║  Status toggle buttons                   ║
        // ╚═══════════════════════════════════════════╝
        const toggleBtn = target.closest<HTMLButtonElement>('.status-toggle-btn');
        if (toggleBtn) {
            const group = toggleBtn.closest<HTMLElement>('.status-toggle-group');
            if (!group) return;
            const val = toggleBtn.dataset.value ?? '';
            const hidden = group.querySelector<HTMLInputElement>('input[type="hidden"]');
            if (hidden) hidden.value = val;
            group.querySelectorAll<HTMLButtonElement>('.status-toggle-btn').forEach(btn => {
                const pressed = btn === toggleBtn;
                btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
                btn.classList.toggle('active', pressed);
            });
            return;
        }

        // ╔═══════════════════════════════════════════╗
        // ║  Unit inline edit — cancel               ║
        // ╚═══════════════════════════════════════════╝
        if (target.closest('.btn-cancel-unit-inline')) {
            const row = target.closest<HTMLTableRowElement>('.unit-row');
            if (!row) return;
            restoreOriginals(row);
            syncToggleButtons(row);
            row.classList.remove('editing');
            return;
        }

        // ╔═══════════════════════════════════════════╗
        // ║  Unit inline edit — save                 ║
        // ╚═══════════════════════════════════════════╝
        if (target.closest('.btn-save-unit-inline')) {
            const row = target.closest<HTMLTableRowElement>('.unit-row');
            if (!row) return;
            const saveBtn = row.querySelector<HTMLButtonElement>('.btn-save-unit-inline')!;
            saveBtn.disabled = true;

            const unitId    = parseInt(row.dataset.unitId    ?? '0', 10);
            const offerId   = parseInt(row.dataset.offerId   ?? '0', 10);
            const offerGroup = row.dataset.offerGroup ?? '';

            const nameInput    = row.querySelector<HTMLInputElement>('[name="name"]');
            const typeSelect   = row.querySelector<HTMLSelectElement>('.cell-type-select');
            const classSelect  = row.querySelector<HTMLSelectElement>('.cell-class-select');
            const serviceSelect = row.querySelector<HTMLSelectElement>('.cell-service-select');
            const activeSelect = row.querySelector<HTMLSelectElement>('[name="active"]');

            const payload = {
                id:              unitId,
                offerId,
                name:            nameInput?.value ?? '',
                offerUnitTypeId: parseInt(typeSelect?.value ?? '0', 10),
                classification:  classSelect?.value ?? '',
                service:         serviceSelect?.value ?? '',
                active:          activeSelect?.value === 'true',
                jsonCustomAttributes: '',
                offerGroupName:  offerGroup,
                groupData:       {} as Record<string, string>,
            };

            try {
                const result = await postJson(urlEditUnit, payload);
                if (result.success) {
                    row.querySelector('.display-name')!.textContent = payload.name;

                    const typeDisplay = row.querySelector('.display-type');
                    if (typeDisplay && typeSelect) {
                        typeDisplay.textContent = typeSelect.options[typeSelect.selectedIndex]?.text ?? '';
                    }

                    const classDisplay = row.querySelector('.display-class');
                    if (classDisplay && classSelect) classDisplay.textContent = classSelect.value;

                    const serviceDisplay = row.querySelector('.display-service');
                    if (serviceDisplay && serviceSelect) serviceDisplay.textContent = serviceSelect.value;

                    const activeDisplay = row.querySelector('.display-active');
                    if (activeDisplay) {
                        activeDisplay.innerHTML = payload.active ? ACTIVE_SVG : INACTIVE_SVG;
                    }

                    // Update data attributes for next edit
                    if (typeSelect) row.dataset.typeId = typeSelect.value;
                    if (classSelect) row.dataset.classification = classSelect.value;
                    if (serviceSelect) row.dataset.service = serviceSelect.value;
                    row.dataset.active = String(payload.active);

                    row.classList.remove('editing');
                    showNotification('Unit updated.', 'success');
                } else {
                    showNotification('Failed to update unit.', 'error');
                }
            } catch {
                showNotification('An error occurred.', 'error');
            }
            saveBtn.disabled = false;
            return;
        }

        // ╔═══════════════════════════════════════════╗
        // ║  Price inline edit — enter edit mode     ║
        // ╚═══════════════════════════════════════════╝
        if (target.closest('.btn-edit-price-inline')) {
            if (!window.matchMedia('(min-width: 992px)').matches) return; // narrow screens use the modal
            const row = target.closest<HTMLTableRowElement>('.price-row');
            if (!row || row.classList.contains('editing')) return;

            // Set status select value from data attribute, then store originals
            const statusSel = row.querySelector<HTMLSelectElement>('[name="status"]');
            if (statusSel) statusSel.value = row.dataset.status ?? '1';
            syncToggleButtons(row);
            storeOriginals(row);

            // Init DateRangePicker on trigger if not yet attached
            const drpTrigger = row.querySelector<HTMLElement>('.drp-trigger-inline');
            if (drpTrigger && !(drpTrigger as any)._drpInstance) {
                const initialFrom = row.dataset.from ?? null;
                const initialTo   = row.dataset.to   ?? null;
                const picker = new DateRangePicker(drpTrigger, {
                    initialFrom,
                    initialTo,
                    onChange: (from, to) => {
                        row.dataset.editFrom = from ?? '';
                        row.dataset.editTo   = to   ?? '';
                    },
                    locale: buildLocale(container)
                });
                (drpTrigger as any)._drpInstance = picker;
                row.dataset.editFrom = initialFrom ?? '';
                row.dataset.editTo   = initialTo   ?? '';
            }

            row.classList.add('editing');
            return;
        }

        // ╔═══════════════════════════════════════════╗
        // ║  Price inline edit — cancel              ║
        // ╚═══════════════════════════════════════════╝
        if (target.closest('.btn-cancel-price-inline')) {
            const row = target.closest<HTMLTableRowElement>('.price-row');
            if (!row) return;
            restoreOriginals(row);
            syncToggleButtons(row);

            // Reset picker to original dates
            const drpTrigger = row.querySelector<HTMLElement>('.drp-trigger-inline');
            const picker = (drpTrigger as any)?._drpInstance as DateRangePicker | undefined;
            if (picker) {
                picker.destroy();
                delete (drpTrigger as any)._drpInstance;
            }
            row.dataset.editFrom = row.dataset.from ?? '';
            row.dataset.editTo   = row.dataset.to   ?? '';

            row.classList.remove('editing');
            return;
        }

        // ╔═══════════════════════════════════════════╗
        // ║  Price inline edit — save                ║
        // ╚═══════════════════════════════════════════╝
        if (target.closest('.btn-save-price-inline')) {
            const row = target.closest<HTMLTableRowElement>('.price-row');
            if (!row) return;
            const saveBtn = row.querySelector<HTMLButtonElement>('.btn-save-price-inline')!;
            saveBtn.disabled = true;

            const priceId    = parseInt(row.dataset.priceId    ?? '0', 10);
            const offerId    = parseInt(row.dataset.offerId    ?? '0', 10);
            const unitId     = parseInt(row.dataset.unitId     ?? '0', 10);
            const offerGroup = row.dataset.offerGroup          ?? '';
            const agencyAcceptsPartnership = row.dataset.agencyAcceptsPartnership !== 'false';

            const priceInput   = row.querySelector<HTMLInputElement>('[name="price"]');
            const partnerInput = row.querySelector<HTMLInputElement>('[name="partnerPriceOverride"]');
            const statusSelect = row.querySelector<HTMLSelectElement>('[name="status"]');
            const seatsInput   = row.querySelector<HTMLInputElement>('[name="numberOfSeats"]');

            const priceVal = parseFloat(priceInput?.value ?? '0') || 0;
            const from = row.dataset.editFrom || row.dataset.from || '';
            const to   = row.dataset.editTo   || row.dataset.to   || '';

            const payload = {
                id:                   priceId,
                offerUnitId:          unitId,
                offerId,
                price:                priceVal,
                partnerPriceOverride: agencyAcceptsPartnership
                    ? (parseFloat(partnerInput?.value ?? '0') || 0)
                    : priceVal,
                from,
                to,
                status:               parseInt(statusSelect?.value ?? '1', 10),
                numberOfSeats:        offerGroup === 'Transport'
                    ? (parseInt(seatsInput?.value ?? '1', 10) || null)
                    : null,
                offerGroupName:       offerGroup,
            };

            try {
                const result = await postJson(urlEditPrice, payload);
                if (result.success) {
                    // Persist new dates on the row for subsequent edits
                    row.dataset.from = from;
                    row.dataset.to   = to;

                    // Update display spans
                    const disp = (cls: string) => row.querySelector(cls);
                    const priceDisplay   = disp('.display-price');
                    const partnerDisplay = disp('.display-partner');
                    const fromDisplay    = disp('.display-from');
                    const toDisplay      = disp('.display-to');
                    const statusDisplay  = disp('.display-status');
                    const seatsDisplay   = disp('.display-seats');

                    if (priceDisplay)   priceDisplay.textContent   = String(priceVal);
                    if (partnerDisplay) partnerDisplay.textContent = ` / ${payload.partnerPriceOverride}`;
                    if (fromDisplay)    fromDisplay.textContent    = from.split('-').reverse().join('.');
                    if (toDisplay)      toDisplay.textContent      = to.split('-').reverse().join('.');
                    if (statusDisplay)  statusDisplay.innerHTML    = payload.status === 1
                        ? `<span class="badge bg-success">${locActive()}</span>`
                        : `<span class="badge bg-secondary">${locInactive()}</span>`;
                    if (seatsDisplay && seatsInput)
                        seatsDisplay.textContent = seatsInput.value;

                    // Update data attribute so next edit opens with fresh status
                    row.dataset.status = String(payload.status);

                    // Destroy picker — will be re-created fresh on next edit
                    const drpTrigger = row.querySelector<HTMLElement>('.drp-trigger-inline');
                    const picker = (drpTrigger as any)?._drpInstance as DateRangePicker | undefined;
                    picker?.destroy();
                    if (drpTrigger) delete (drpTrigger as any)._drpInstance;

                    row.classList.remove('editing');
                    showNotification('Price updated.', 'success');
                } else {
                    showNotification('Failed to update price.', 'error');
                }
            } catch {
                showNotification('An error occurred.', 'error');
            }
            saveBtn.disabled = false;
            return;
        }
    });
}
